import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Admin page for PF/Gratuity governance (2026-10-02).
 *
 * Minimal shell that exercises the ``/api/payroll/company-pf-settings``
 * and ``/api/payroll/company-gratuity-settings`` endpoints, lists active
 * settings, and offers a guarded create form. The per-employee profile
 * editor is linked via a search-by-employee-id micro-form.
 */

const API = process.env.REACT_APP_BACKEND_URL || "";

function authHeader() {
  const t = localStorage.getItem("token") || localStorage.getItem("auth_token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiGet(path) {
  const r = await axios.get(`${API}${path}`, { headers: authHeader() });
  return r.data;
}

async function apiPost(path, body) {
  const r = await axios.post(`${API}${path}`, body, { headers: authHeader() });
  return r.data;
}

async function apiPatch(path, body) {
  const r = await axios.patch(`${API}${path}`, body, { headers: authHeader() });
  return r.data;
}


function Section({ title, children, right }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 mb-6" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        <div>{right}</div>
      </div>
      {children}
    </section>
  );
}


function CompanySettingsTable({ kind, items, onReload }) {
  const archive = async (id) => {
    if (!confirm(`Archive ${kind} setting ${id}?`)) return;
    try {
      await apiPost(`/api/payroll/company-${kind}-settings/${id}/archive`);
      onReload();
    } catch (e) {
      alert(`Archive failed: ${e.response?.data?.detail || e.message}`);
    }
  };
  return (
    <table className="w-full text-sm" data-testid={`${kind}-settings-table`}>
      <thead>
        <tr className="text-left text-slate-500 border-b">
          <th className="py-2 pr-3">Company</th>
          <th className="py-2 pr-3">{kind === "pf" ? "Covered" : "Applicable"}</th>
          <th className="py-2 pr-3">Effective From</th>
          <th className="py-2 pr-3">{kind === "pf" ? "Wage Ceiling Override" : "Accrual Rate Override"}</th>
          <th className="py-2 pr-3">Status</th>
          <th className="py-2 pr-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr>
            <td colSpan={6} className="py-4 text-slate-400 italic">
              No settings on file. Click "Add new version" to seed one.
            </td>
          </tr>
        )}
        {items.map((it) => (
          <tr key={it.id} className="border-b border-slate-100" data-testid={`row-${it.id}`}>
            <td className="py-2 pr-3 font-mono text-xs">{it.company_id}</td>
            <td className="py-2 pr-3">
              <span className={`px-2 py-0.5 rounded text-xs ${(kind === "pf" ? it.is_covered : it.is_applicable) ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>
                {(kind === "pf" ? it.is_covered : it.is_applicable) ? "Yes" : "No"}
              </span>
            </td>
            <td className="py-2 pr-3 font-mono text-xs">{it.effective_from}</td>
            <td className="py-2 pr-3 font-mono text-xs">
              {(kind === "pf" ? it.wage_ceiling_override : it.accrual_rate_override) ?? "—"}
            </td>
            <td className="py-2 pr-3 text-xs uppercase">{it.status}</td>
            <td className="py-2 pr-3">
              {it.status === "active" && (
                <button className="text-xs text-rose-600 hover:underline" data-testid={`archive-${it.id}`} onClick={() => archive(it.id)}>
                  Archive
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function AddCompanySettingForm({ kind, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    company_id: "REEDOS",
    [kind === "pf" ? "is_covered" : "is_applicable"]: true,
    effective_from: new Date().toISOString().slice(0, 10),
    [kind === "pf" ? "wage_ceiling_override" : "accrual_rate_override"]: "",
    notes: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    const overrideKey = kind === "pf" ? "wage_ceiling_override" : "accrual_rate_override";
    payload[overrideKey] = payload[overrideKey] === "" ? null : Number(payload[overrideKey]);
    try {
      await apiPost(`/api/payroll/company-${kind}-settings`, payload);
      setOpen(false);
      onSaved();
    } catch (err) {
      alert(`Save failed: ${err.response?.data?.detail || err.message}`);
    }
  };
  return (
    <>
      <button
        className="text-sm px-3 py-1 rounded border border-slate-300 hover:bg-slate-50"
        data-testid={`add-${kind}-setting-btn`}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Cancel" : "Add new version"}
      </button>
      {open && (
        <form onSubmit={submit} className="mt-3 border-t pt-3 space-y-2 text-sm" data-testid={`${kind}-add-form`}>
          <div className="flex gap-2">
            <input className="border rounded px-2 py-1 flex-1" placeholder="Company ID (REEDOS / JAMAPUNJI)"
              value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              data-testid={`${kind}-company-id-input`} />
            <input type="date" className="border rounded px-2 py-1"
              value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              data-testid={`${kind}-effective-from-input`} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input type="checkbox"
                checked={kind === "pf" ? form.is_covered : form.is_applicable}
                onChange={(e) => setForm({ ...form, [kind === "pf" ? "is_covered" : "is_applicable"]: e.target.checked })}
                data-testid={`${kind}-covered-checkbox`} />
              {kind === "pf" ? "Company IS an EPF establishment" : "Payment of Gratuity Act applies"}
            </label>
            <input className="border rounded px-2 py-1 flex-1"
              placeholder={kind === "pf" ? "Wage ceiling override (optional)" : "Accrual rate override, e.g. 0.0481 (optional)"}
              value={form[kind === "pf" ? "wage_ceiling_override" : "accrual_rate_override"]}
              onChange={(e) => setForm({ ...form, [kind === "pf" ? "wage_ceiling_override" : "accrual_rate_override"]: e.target.value })}
              data-testid={`${kind}-override-input`} />
          </div>
          <input className="border rounded px-2 py-1 w-full"
            placeholder="Notes / supporting evidence reference" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid={`${kind}-notes-input`} />
          <button type="submit" className="px-3 py-1 bg-slate-800 text-white rounded text-sm" data-testid={`${kind}-save-btn`}>
            Save new version
          </button>
          <p className="text-xs text-slate-500 mt-1">
            Previous active version for the same company will remain visible but the engine will
            pick the highest effective_from &le; the run period.
          </p>
        </form>
      )}
    </>
  );
}


function EmployeeProfileEditor({ kind }) {
  const [eid, setEid] = useState("");
  const [employee, setEmployee] = useState(null);
  const [status, setStatus] = useState("mandatory");
  const [reason, setReason] = useState("");
  const [evidenceObj, setEvidenceObj] = useState("");
  const [approverId, setApproverId] = useState("");
  const [vpf, setVpf] = useState("");
  const [actual, setActual] = useState(false);
  const [uan, setUan] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setMsg("");
    try {
      const data = await apiGet(`/api/employee/${eid}`);
      setEmployee(data);
      const prof = (kind === "pf" ? data.pf_profile : data.gratuity_profile) || {};
      setStatus(prof.status || "pending_verification");
      setUan(prof.uan || "");
      setVpf(prof.vpf_amount || "");
      setActual(!!prof.contribute_on_actual_wages);
    } catch (e) {
      setMsg(`Load failed: ${e.response?.data?.detail || e.message}`);
    }
  };
  const save = async () => {
    const payload = { status, reason, evidence_object_id: evidenceObj || null, approver_id: approverId || null };
    if (kind === "pf") {
      payload.uan = uan || null;
      payload.vpf_amount = vpf === "" ? null : Number(vpf);
      payload.contribute_on_actual_wages = actual;
    }
    try {
      const r = await apiPatch(`/api/payroll/employee/${eid}/${kind}-profile`, payload);
      setMsg(`Saved. History row id: ${r.history_event?.at || "ok"}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = d?.code || "ERROR";
      const text = d?.message || e.message;
      setMsg(`${code}: ${text}`);
    }
  };

  return (
    <div className="text-sm space-y-2" data-testid={`${kind}-profile-editor`}>
      <div className="flex gap-2">
        <input className="border rounded px-2 py-1 flex-1 font-mono"
          placeholder="Employee ID (EMP-...)" value={eid}
          onChange={(e) => setEid(e.target.value.trim())}
          data-testid={`${kind}-emp-id-input`} />
        <button className="px-3 py-1 border rounded" onClick={load}
          data-testid={`${kind}-load-emp-btn`}>Load</button>
      </div>
      {employee && (
        <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-2">
          <div className="text-xs text-slate-500">
            Loaded: <span className="font-mono">{employee.full_name} · {employee.employee_code}</span>
          </div>
          <div className="flex gap-2 items-center">
            <label>Status:</label>
            <select className="border rounded px-2 py-1" value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid={`${kind}-status-select`}>
              <option value="mandatory">Mandatory</option>
              {kind === "pf" && <option value="voluntary">Voluntary</option>}
              <option value="excluded">Excluded</option>
              <option value="pending_verification">Pending Verification</option>
            </select>
            {kind === "pf" && (
              <label className="flex items-center gap-1 ml-4 text-xs">
                <input type="checkbox" checked={actual}
                  onChange={(e) => setActual(e.target.checked)}
                  data-testid="pf-actual-wages-checkbox" />
                Contribute on actual Basic (uncaps ₹1,800)
              </label>
            )}
          </div>
          {kind === "pf" && (
            <div className="flex gap-2">
              <input className="border rounded px-2 py-1 flex-1" placeholder="UAN (prior PF member)"
                value={uan} onChange={(e) => setUan(e.target.value)}
                data-testid="pf-uan-input" />
              <input className="border rounded px-2 py-1 w-40" placeholder="VPF monthly ₹"
                type="number" value={vpf} onChange={(e) => setVpf(e.target.value)}
                data-testid="pf-vpf-input" />
            </div>
          )}
          <input className="border rounded px-2 py-1 w-full"
            placeholder="Reason (≥ 10 chars for sensitive transitions)"
            value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid={`${kind}-reason-input`} />
          <div className="flex gap-2">
            <input className="border rounded px-2 py-1 flex-1" placeholder="Evidence object_id"
              value={evidenceObj} onChange={(e) => setEvidenceObj(e.target.value)}
              data-testid={`${kind}-evidence-input`} />
            <input className="border rounded px-2 py-1 flex-1" placeholder="Approver user_id (distinct from you)"
              value={approverId} onChange={(e) => setApproverId(e.target.value)}
              data-testid={`${kind}-approver-input`} />
          </div>
          <button className="px-3 py-1 bg-slate-800 text-white rounded"
            onClick={save} data-testid={`${kind}-save-profile-btn`}>
            Save (audit-gated)
          </button>
          {msg && <div className={`text-xs mt-1 ${msg.startsWith("Saved") ? "text-emerald-700" : "text-rose-600"}`} data-testid={`${kind}-save-msg`}>{msg}</div>}
        </div>
      )}
    </div>
  );
}


export default function PfGovernancePage() {
  const [pfItems, setPfItems] = useState([]);
  const [gItems, setGItems] = useState([]);

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        apiGet("/api/payroll/company-pf-settings"),
        apiGet("/api/payroll/company-gratuity-settings"),
      ]);
      setPfItems(a.items || []);
      setGItems(b.items || []);
    } catch (e) {
      console.error("load failed", e);
    }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="pf-governance-page">
      <h2 className="text-xl font-semibold mb-1">PF &amp; Gratuity Governance</h2>
      <p className="text-sm text-slate-500 mb-5">
        Dynamic applicability at the <strong>company</strong> and <strong>employee</strong> level.
        A global PF-off flag does <em>not</em> silently defeat mandatory employee coverage — any
        disagreement raises a blocking <code>CONFLICTING_PF_CONFIGURATION</code> exception.
      </p>

      <Section title="Company PF Settings" right={<AddCompanySettingForm kind="pf" onSaved={loadAll} />}>
        <CompanySettingsTable kind="pf" items={pfItems} onReload={loadAll} />
      </Section>

      <Section title="Company Gratuity Settings" right={<AddCompanySettingForm kind="gratuity" onSaved={loadAll} />}>
        <CompanySettingsTable kind="gratuity" items={gItems} onReload={loadAll} />
      </Section>

      <Section title="Employee PF Profile (audit-gated)">
        <EmployeeProfileEditor kind="pf" />
      </Section>

      <Section title="Employee Gratuity Profile (audit-gated)">
        <EmployeeProfileEditor kind="gratuity" />
      </Section>
    </div>
  );
}
